import { Link } from "react-router-dom";
import { Heart, Sparkles } from "lucide-react";
import { LockBadge } from "@/components/LockBadge";
import { productUrl } from "@/lib/productUrl";
import { pickVariantForColor } from "@/lib/colorMatch";

export function ProductCard({ product, index = 0, isFav, onFav, tall = false, focusColor = null }) {
  const href = productUrl(product);
  const tryOnHref = `${href}?tryon=1`;
  // When the parent listing has a color filter active, surface the variant
  // whose color actually matches — otherwise the card looks broken (user
  // searches "Coral" but sees the Black primary image).
  const shown = pickVariantForColor(product, focusColor);
  const otherColorsCount = Math.max(
    0,
    (product.variants?.length || product.colors?.length || 1) - 1
  );
  // AI Try-On only supports dresses — hide the 1-click "Try This Look" pill on
  // tuxedos, accessories, and shoes so we never route users into a doomed flow.
  const tryOnEligible = !["tuxedos", "accessories", "shoes"].includes(product.category_slug);
  // Two flavors of "unavailable":
  //   • is_sold_out — boutique physically ran out (set by admin).
  //   • taken       — locked at this school by another student.
  // Sold-out beats taken visually since it means the dress cannot be ordered at all.
  const soldOut = !!product.is_sold_out;
  const taken = !!product.taken && !soldOut;
  const unavailable = soldOut || taken;
  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group relative animate-fade-up"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <Link to={href}>
        <div className={`relative overflow-hidden bg-blush ${tall ? "aspect-[3/5]" : "aspect-[3/4]"}`}>
          {product.lock && <LockBadge lock={product.lock} mine={product.lock_mine} floating />}
          <img
            src={shown.image}
            alt={product.name}
            className={`w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${unavailable ? "grayscale opacity-60" : ""}`}
          />
          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors duration-500" />
          {soldOut && (
            <>
              {/* Diagonal "SOLD OUT" ribbon — editorial, not a screamy sticker */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -right-14 top-6 rotate-[35deg] bg-ink text-ivory font-label text-[9px] tracking-[0.35em] px-14 py-1.5 shadow-lg">
                  SOLD OUT
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/85 to-transparent p-4">
                <span data-testid={`sold-out-badge-${product.id}`} className="font-label text-[10px] text-ivory tracking-[0.28em]">
                  NO LONGER AVAILABLE
                </span>
              </div>
            </>
          )}
          {taken && (
            <div className="absolute inset-0 flex items-center justify-center bg-ink/25">
              <span data-testid={`taken-badge-${product.id}`} className="font-label text-[10px] text-ivory bg-ink/80 px-4 py-2 rounded-sm">Taken at your school</span>
            </div>
          )}
          {product.trend && !unavailable && (
            <span className="absolute top-4 right-4 font-label text-[9px] text-ink bg-champagne px-2.5 py-1">
              Trending
            </span>
          )}
        </div>
      </Link>
      {/* Try This Look — 1-click try-on jump (dresses only, still available) */}
      {!unavailable && tryOnEligible && (
        <Link
          to={tryOnHref}
          onClick={(e) => e.stopPropagation()}
          data-testid={`try-look-${product.id}`}
          className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-1.5 glass-dark text-ivory px-3 py-1.5 rounded-full font-label text-[9px] tracking-widest hover:bg-rosemetal hover:text-ivory z-10"
        >
          <Sparkles className="w-3 h-3" /> TRY THIS LOOK
        </Link>
      )}
      <div className="flex items-start justify-between mt-3">
        <div>
          <Link to={href}>
            <h3 className={`font-display text-xl leading-none ${soldOut ? "text-ink/50" : ""}`}>{product.name}</h3>
          </Link>
          <p className="font-body text-xs text-muted-foreground mt-1 tracking-wide">
            <span className="capitalize">{shown.color}</span>{otherColorsCount > 0 ? <span className="text-ink/60"> +{otherColorsCount} colors</span> : null} · {product.occasion}
          </p>
        </div>
        <div className="text-right flex flex-col items-end gap-1">
          <span className={`font-body text-sm ${soldOut ? "line-through text-ink/40" : ""}`}>${product.price}</span>
          {onFav && (
            <button
              data-testid={`fav-btn-${product.id}`}
              onClick={(e) => { e.preventDefault(); onFav(product.id); }}
              aria-label="Save to favorites"
            >
              <Heart className={`w-4 h-4 transition-colors ${isFav ? "fill-rosemetal text-rosemetal" : "text-ink hover:text-rosemetal"}`} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
